import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Segment, Header, Button, Icon, Message, Progress, Form, Radio, Divider } from 'semantic-ui-react';
import GlassPageWrapper from '../../../components/Layout/GlassPageWrapper';
import { quizAttemptService, QuizAttemptDto, SubmitAnswerDto, AvailableQuizDto } from '../../../services/learning/QuizAttemptService';
import { quizService, QuizDto } from '../../../services/learning/QuizService';
import { useQuiz } from '../../../context/QuizContext';
import QuizExitConfirmModal from '../../../components/Quiz/QuizExitConfirmModal';
import { useQuizNavigationBlocker } from '../../../hooks/useQuizNavigationBlocker';

const QuizTakingPage: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { startQuiz, endQuiz } = useQuiz();

  // Adicionar estilos de animação
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes pulse {
        0% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.05);
        }
        100% {
          transform: scale(1);
        }
      }

      @keyframes glow {
        0% {
          box-shadow: 0 0 5px rgba(102, 126, 234, 0.5);
        }
        50% {
          box-shadow: 0 0 20px rgba(102, 126, 234, 0.8), 0 0 30px rgba(102, 126, 234, 0.6);
        }
        100% {
          box-shadow: 0 0 5px rgba(102, 126, 234, 0.5);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [quiz, setQuiz] = useState<QuizDto | null>(null);
  const [attempt, setAttempt] = useState<QuizAttemptDto | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [showExitModal, setShowExitModal] = useState(false);
  const [quizAvailability, setQuizAvailability] = useState<AvailableQuizDto | null>(null);
  const [isQuizUnavailable, setIsQuizUnavailable] = useState(false);
  const hasStartedAttempt = React.useRef(false);

  useEffect(() => {
    const loadQuiz = async () => {
      if (!quizId) return;

      // Evitar múltiplas execuções - se já iniciou tentativa, não executar novamente
      if (hasStartedAttempt.current || attempt) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setIsQuizUnavailable(false);

        // Verificar se dados foram pré-carregados
        const preloadedQuiz = location.state?.preloadedQuiz;
        const preloadedAvailability = location.state?.preloadedAvailability;

        if (preloadedQuiz && preloadedAvailability) {
          // Usar dados pré-carregados (muito mais rápido - evita 2 requisições)
          setQuiz(preloadedQuiz);
          setQuizAvailability(preloadedAvailability);
          
          if (!preloadedAvailability.isAvailable) {
            setIsQuizUnavailable(true);
            setError(preloadedAvailability.unavailableReason || 'Este questionário não está disponível no momento.');
            setLoading(false);
            return;
          }

          // Marcar que tentativa está sendo iniciada
          hasStartedAttempt.current = true;

          // Apenas iniciar tentativa (única requisição necessária)
          const attemptData = await quizAttemptService.startQuizAttempt({
            quizId,
            trainingClassId: undefined
          });
          setAttempt(attemptData);
          startQuiz(attemptData.id, preloadedQuiz.id, attemptData.startedAt);
          
          if (preloadedQuiz.timeLimitMinutes) {
            setTimeRemaining(preloadedQuiz.timeLimitMinutes * 60);
          }
        } else {
          // Fallback: carregar normalmente (otimizado com paralelização)
          const [quizData, availableQuizzes] = await Promise.all([
            quizService.getQuiz(quizId).catch(() => null),
            quizAttemptService.getAvailableQuizzes().catch(() => [])
          ]);

          const currentQuizAvailability = availableQuizzes.find(q => q.id === quizId);
          
          if (!currentQuizAvailability || !quizData) {
            setError('Questionário não encontrado ou não disponível para você.');
            setIsQuizUnavailable(true);
            setLoading(false);
            return;
          }

          if (!currentQuizAvailability.isAvailable) {
            setQuizAvailability(currentQuizAvailability);
            setIsQuizUnavailable(true);
            setError(currentQuizAvailability.unavailableReason || 'Este questionário não está disponível no momento.');
            setLoading(false);
            return;
          }

          setQuiz(quizData);
          setQuizAvailability(currentQuizAvailability);

          // Marcar que tentativa está sendo iniciada
          hasStartedAttempt.current = true;

          const attemptData = await quizAttemptService.startQuizAttempt({
            quizId,
            trainingClassId: undefined
          });
          setAttempt(attemptData);
          startQuiz(attemptData.id, quizData.id, attemptData.startedAt);

          if (quizData.timeLimitMinutes) {
            setTimeRemaining(quizData.timeLimitMinutes * 60);
          }
        }
      } catch (err: any) {
        // Resetar flag em caso de erro para permitir retry
        hasStartedAttempt.current = false;
        
        // Verificar se o erro é relacionado a tentativas esgotadas
        const errorMessage = err.message || 'Erro ao carregar questionário';
        if (errorMessage.includes('tentativa') || errorMessage.includes('disponível') || errorMessage.includes('Não é possível iniciar')) {
          setIsQuizUnavailable(true);
          // Tentar obter informações de disponibilidade para mostrar mensagem melhor
          try {
            const availableQuizzes = await quizAttemptService.getAvailableQuizzes();
            const currentQuizAvailability = availableQuizzes.find(q => q.id === quizId);
            if (currentQuizAvailability) {
              setQuizAvailability(currentQuizAvailability);
              setError(currentQuizAvailability.unavailableReason || errorMessage);
            } else {
              setError(errorMessage);
            }
          } catch {
            setError(errorMessage);
          }
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    loadQuiz();
  }, [quizId, startQuiz]);

  useEffect(() => {
    if (timeRemaining === null || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev === null || prev <= 1) {
          handleCompleteQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining]);

  // Verificar expiração periodicamente (a cada minuto)
  useEffect(() => {
    if (!attempt || attempt.completedAt) return;

    const checkExpiration = () => {
      const startTime = new Date(attempt.startedAt).getTime();
      const currentTime = Date.now();
      const diffMinutes = (currentTime - startTime) / (1000 * 60);
      
      if (diffMinutes > 20) {
        // Tentativa expirada
        try {
          quizAttemptService.abandonQuizAttempt(attempt.id);
        } catch (err) {
          console.warn('Erro ao marcar tentativa como abandonada:', err);
        }
        
        endQuiz();
        setError('Sua tentativa expirou após 20 minutos de inatividade.');
        setTimeout(() => {
          navigate('/learning');
        }, 2000);
      }
    };

    // Verificar imediatamente
    checkExpiration();

    // Verificar a cada minuto
    const interval = setInterval(checkExpiration, 60000);

    return () => clearInterval(interval);
  }, [attempt, endQuiz, navigate]);

  // Bloquear navegação quando quiz está em andamento
  useQuizNavigationBlocker({
    isBlocked: !!attempt && !attempt.completedAt,
    onNavigationAttempt: () => {
      setShowExitModal(true);
    },
    startedAt: attempt?.startedAt,
    attemptId: attempt?.id,
  });

  // Limpar contexto ao desmontar (se ainda estiver em progresso)
  useEffect(() => {
    return () => {
      if (attempt && !attempt.completedAt) {
        endQuiz();
      }
      // Resetar flag ao desmontar
      hasStartedAttempt.current = false;
    };
  }, [attempt, endQuiz]);

  // Resetar flag quando quizId mudar
  useEffect(() => {
    hasStartedAttempt.current = false;
  }, [quizId]);

  const handleAnswerChange = async (questionId: string, optionId: string) => {
    if (!attempt) return;

    setAnswers(prev => ({ ...prev, [questionId]: optionId }));

    try {
      await quizAttemptService.submitAnswer(attempt.id, {
        questionId,
        questionOptionId: optionId
      });
    } catch (err: any) {
      console.error('Erro ao salvar resposta:', err);
    }
  };

  const handleCompleteQuiz = async () => {
    if (!attempt || !quiz) return;

    // Verificar se todas as questões foram respondidas
    const answeredQuestions = Object.keys(answers).length;
    const totalQuestions = quiz.questions.length;
    
    if (answeredQuestions < totalQuestions) {
      setError(`Por favor, responda todas as ${totalQuestions} questões antes de finalizar.`);
      return;
    }

    try {
      setSubmitting(true);
      const completedAttempt = await quizAttemptService.completeQuizAttempt(attempt.id);
      setAttempt(completedAttempt);
      
      // Finalizar quiz no contexto
      endQuiz();
      
      // Redirecionar para resultado
      navigate(`/learning/quizzes/${quizId}/result`, { state: { attempt: completedAttempt } });
    } catch (err: any) {
      setError(err.message || 'Erro ao finalizar questionário');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelClick = () => {
    setShowExitModal(true);
  };

  const handleConfirmExit = () => {
    if (attempt) {
      endQuiz();
      setShowExitModal(false);
      navigate('/learning');
    }
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <GlassPageWrapper title="Carregando Questionário" icon="spinner">
        <Message info>
          <Icon name="spinner" loading />
          Carregando questionário...
        </Message>
      </GlassPageWrapper>
    );
  }

  // Mostrar tela de quiz indisponível
  if (isQuizUnavailable) {
    return (
      <GlassPageWrapper 
        title={quizAvailability?.title || quiz?.title || 'Questionário Indisponível'} 
        icon="lock"
      >
        <Segment style={{ maxWidth: '800px', margin: '0 auto' }}>
          <Message warning size="large">
            <Message.Header>
              <Icon name="lock" />
              Questionário Indisponível
            </Message.Header>
            <p style={{ fontSize: '1.1em', marginTop: '10px' }}>
              {error || quizAvailability?.unavailableReason || 'Este questionário não está disponível no momento.'}
            </p>
          </Message>

          {quizAvailability && (
            <div style={{ marginTop: '20px' }}>
              <Segment>
                <Header as="h4">
                  <Icon name="info circle" />
                  Informações do Questionário
                </Header>
                <div style={{ marginTop: '15px' }}>
                  <p><strong>Título:</strong> {quizAvailability.title}</p>
                  {quizAvailability.description && (
                    <p><strong>Descrição:</strong> {quizAvailability.description}</p>
                  )}
                  {quizAvailability.category && (
                    <p><strong>Categoria:</strong> {quizAvailability.category}</p>
                  )}
                  {typeof quizAvailability.remainingAttempts === 'number' && (
                    <Message 
                      info 
                      style={{ marginTop: '15px' }}
                    >
                      <Message.Header>
                        <Icon name="repeat" />
                        Tentativas Restantes
                      </Message.Header>
                      <p>
                        Você possui <strong>{quizAvailability.remainingAttempts}</strong> tentativa{quizAvailability.remainingAttempts === 1 ? '' : 's'} restante{quizAvailability.remainingAttempts === 1 ? '' : 's'} para este questionário.
                      </p>
                      {quizAvailability.remainingAttempts === 0 && (
                        <p style={{ marginTop: '10px', color: '#db2828' }}>
                          <Icon name="exclamation triangle" />
                          Você atingiu o limite máximo de tentativas. Entre em contato com um administrador se precisar de tentativas extras.
                        </p>
                      )}
                    </Message>
                  )}
                  {quizAvailability.releaseDate && new Date(quizAvailability.releaseDate) > new Date() && (
                    <Message info style={{ marginTop: '15px' }}>
                      <Message.Header>
                        <Icon name="calendar" />
                        Data de Liberação
                      </Message.Header>
                      <p>
                        Este questionário será liberado em: <strong>
                          {new Date(quizAvailability.releaseDate).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </strong>
                      </p>
                    </Message>
                  )}
                </div>
              </Segment>
            </div>
          )}

          <div style={{ marginTop: '30px', textAlign: 'center' }}>
            <Button 
              primary 
              size="large"
              onClick={() => navigate('/learning')}
              icon="arrow left"
              content="Voltar ao Centro de Aprendizado"
            />
          </div>
        </Segment>
      </GlassPageWrapper>
    );
  }

  if (error || !quiz || !attempt) {
    return (
      <GlassPageWrapper title="Erro" icon="exclamation triangle">
        <Message negative>
          <Message.Header>Erro</Message.Header>
          <p>{error || 'Questionário não encontrado'}</p>
          <Button onClick={() => navigate('/learning')}>Voltar</Button>
        </Message>
      </GlassPageWrapper>
    );
  }

  // Calcular progresso do quiz
  const answeredQuestions = Object.keys(answers).length;
  const totalQuestions = quiz.questions.length;
  const progressPercentage = (answeredQuestions / totalQuestions) * 100;

  return (
    <GlassPageWrapper title={quiz.title} icon="question circle">
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '0 20px'
      }}>
        {/* Barra de progresso moderna */}
        <div style={{
          marginBottom: '15px',
          padding: '16px',
          background: '#2185d0',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(33, 133, 208, 0.3)',
          color: 'white'
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '1em', fontWeight: '600' }}>
              Progresso do Questionário
            </h3>
            <p style={{ margin: '0', opacity: '0.9', fontSize: '0.85em' }}>
              {answeredQuestions} de {totalQuestions} questões respondidas
            </p>
          </div>
          {timeRemaining !== null && (
            <div style={{
              background: timeRemaining < 300 ? '#db2828' : '#f2711c',
              padding: '6px 12px',
              borderRadius: '20px',
              fontWeight: 'bold',
              fontSize: '0.85em',
              boxShadow: '0 3px 12px rgba(0,0,0,0.2)'
            }}>
              <Icon name="clock" style={{ marginRight: '4px' }} />
              {formatTime(timeRemaining)}
            </div>
          )}
        </div>

        <div style={{
          width: '100%',
          height: '12px',
          background: 'rgba(255,255,255,0.2)',
          borderRadius: '25px',
          overflow: 'hidden',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            width: `${progressPercentage}%`,
            height: '100%',
            background: progressPercentage === 100
              ? '#21ba45'
              : 'rgba(255, 255, 255, 0.9)',
            borderRadius: '25px',
            transition: 'width 0.5s ease-in-out, background 0.3s ease',
            boxShadow: progressPercentage === 100
              ? '0 0 15px rgba(33, 186, 69, 0.6)'
              : '0 0 8px rgba(255, 255, 255, 0.5)',
            animation: progressPercentage === 100 ? 'pulse 2s infinite' : 'none'
          }} />
        </div>
      </div>

      {/* Informações do quiz */}
      <div style={{
        background: '#a333c8',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '20px',
        color: 'white',
        boxShadow: '0 4px 12px rgba(163, 51, 200, 0.3)'
      }}>
        <Header as="h3" style={{ color: 'white', marginBottom: '8px', fontSize: '1.1em' }}>
          {quiz.description || 'Responda todas as questões'}
        </Header>
        <p style={{ margin: '0', opacity: '0.9', fontSize: '0.9em' }}>
          <Icon name="trophy" />
          Pontuação mínima para aprovação: {quiz.minimumScore}%
        </p>
      </div>

      <Form>
        {quiz.questions.map((question, index) => {
          const isAnswered = answers[question.id] !== undefined;
          return (
            <div
              key={question.id}
              style={{
                marginBottom: '18px',
                background: '#ffffff',
                borderRadius: '16px',
                padding: '18px',
                boxShadow: isAnswered
                  ? '0 8px 32px rgba(33, 186, 69, 0.15), 0 0 16px rgba(33, 186, 69, 0.1)'
                  : '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: isAnswered ? '2px solid #21ba45' : '2px solid transparent',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                animation: `slideInUp 0.6s ease-out ${index * 0.1}s both`
              }}
            >
              {/* Indicador de status */}
              <div style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: isAnswered ? '#21ba45' : '#f2711c',
                boxShadow: `0 0 12px ${isAnswered ? 'rgba(33, 186, 69, 0.6)' : 'rgba(242, 113, 28, 0.6)'}`
              }} />

              {/* Cabeçalho da questão */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                <div style={{
                  background: '#2185d0',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '0.85em',
                  fontWeight: '600',
                  boxShadow: '0 2px 8px rgba(33, 133, 208, 0.3)'
                }}>
                  Questão {index + 1}
                </div>
                <div style={{
                  background: '#a333c8',
                  color: 'white',
                  padding: '5px 11px',
                  borderRadius: '18px',
                  fontSize: '0.75em',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Icon name="star" size="small" />
                  {question.points} pontos
                </div>
              </div>

              {/* Texto da questão */}
              <div style={{
                background: '#f8f9fa',
                padding: '16px',
                borderRadius: '12px',
                marginBottom: '16px',
                border: '1px solid rgba(0,0,0,0.1)'
              }}>
                <p style={{
                  margin: '0',
                  fontSize: '1em',
                  lineHeight: '1.5',
                  color: '#2c3e50',
                  fontWeight: '500'
                }}>
                  {question.text}
                </p>
              </div>

              {/* Opções de resposta */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {question.options.map(option => {
                  const isSelected = answers[question.id] === option.id;
                  return (
                    <div
                      key={option.id}
                      onClick={() => handleAnswerChange(question.id, option.id)}
                      style={{
                        padding: '14px 16px',
                        borderRadius: '10px',
                        border: `2px solid ${isSelected ? '#2185d0' : 'rgba(0,0,0,0.1)'}`,
                        background: isSelected
                          ? '#2185d0'
                          : 'white',
                        color: isSelected ? 'white' : '#2c3e50',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: isSelected
                          ? '0 4px 12px rgba(33, 133, 208, 0.4)'
                          : '0 2px 8px rgba(0,0,0,0.05)',
                        transform: isSelected ? 'translateY(-2px)' : 'translateY(0)',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          border: `2px solid ${isSelected ? 'white' : '#2185d0'}`,
                          background: isSelected ? 'white' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: '0'
                        }}>
                          {isSelected && (
                            <div style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: '#2185d0'
                            }} />
                          )}
                        </div>
                        <span style={{
                          fontSize: '0.95em',
                          fontWeight: isSelected ? '600' : '400',
                          lineHeight: '1.4'
                        }}>
                          {option.text}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Barra de ação moderna */}
        <div style={{
          background: '#f8f9fa',
          borderRadius: '16px',
          padding: '20px',
          marginTop: '24px',
          border: '1px solid rgba(0,0,0,0.1)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            {/* Botão Cancelar */}
            <button
              onClick={handleCancelClick}
              style={{
                background: '#db2828',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '40px',
                fontSize: '0.9em',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(219, 40, 40, 0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(219, 40, 40, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(219, 40, 40, 0.4)';
              }}
            >
              <Icon name="times" />
              Cancelar
            </button>

            {/* Status do progresso */}
            <div style={{
              flex: '1',
              textAlign: 'center',
              padding: '0 16px'
            }}>
              <div style={{
                fontSize: '0.85em',
                color: '#6c757d',
                marginBottom: '6px',
                fontWeight: '500'
              }}>
                Status do Questionário
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: answeredQuestions === totalQuestions ? '#21ba45' : '#f2711c',
                  boxShadow: `0 0 8px ${answeredQuestions === totalQuestions ? 'rgba(33, 186, 69, 0.6)' : 'rgba(242, 113, 28, 0.6)'}`
                }} />
                <span style={{
                  fontSize: '1em',
                  fontWeight: '600',
                  color: answeredQuestions === totalQuestions ? '#21ba45' : '#f2711c'
                }}>
                  {answeredQuestions === totalQuestions ? 'Completo' : 'Em andamento'}
                </span>
              </div>
            </div>

            {/* Botão Finalizar */}
            <button
              onClick={handleCompleteQuiz}
              disabled={submitting || answeredQuestions !== totalQuestions}
              style={{
                background: answeredQuestions === totalQuestions
                  ? '#21ba45'
                  : '#767676',
                color: 'white',
                border: 'none',
                padding: '14px 28px',
                borderRadius: '40px',
                fontSize: '1em',
                fontWeight: '700',
                cursor: answeredQuestions === totalQuestions && !submitting ? 'pointer' : 'not-allowed',
                transition: 'all 0.3s ease',
                boxShadow: answeredQuestions === totalQuestions
                  ? '0 4px 12px rgba(33, 186, 69, 0.5)'
                  : '0 2px 8px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                opacity: (submitting || answeredQuestions !== totalQuestions) ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (answeredQuestions === totalQuestions && !submitting) {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(33, 186, 69, 0.7)';
                }
              }}
              onMouseLeave={(e) => {
                if (answeredQuestions === totalQuestions && !submitting) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(33, 186, 69, 0.5)';
                }
              }}
            >
              {submitting ? (
                <>
                  <Icon name="spinner" loading />
                  Finalizando...
                </>
              ) : (
                <>
                  <Icon name="check" />
                  {answeredQuestions === totalQuestions ? 'Finalizar Questionário' : 'Aguardando respostas'}
                </>
              )}
            </button>
          </div>
        </div>
      </Form>
      </div>

      <QuizExitConfirmModal
        open={showExitModal}
        onConfirm={handleConfirmExit}
        onCancel={handleCancelExit}
      />
    </GlassPageWrapper>
  );
};

export default QuizTakingPage;

